import { useEffect, useState } from "react";
import { Eye, TrendingUp, QrCode, Share2 } from "lucide-react";
import api from "@/lib/api";

const RANGES = [[7, "7 days"], [30, "30 days"], [90, "90 days"]];

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [scans, setScans] = useState(null);
  const [shares, setShares] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics", { params: { days } }).then(({ data }) => setData(data)).catch(() => {});
    api.get("/admin/qr-scans", { params: { days } }).then(({ data }) => setScans(data)).catch(() => {});
    api.get("/admin/share-stats").then(({ data }) => setShares(data)).catch(() => {});
  }, [days]);

  const maxDaily = Math.max(1, ...((data?.daily || []).map((d) => d.views)));

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl">Store Analytics</h1>
          <p className="font-body text-sm text-ink/50 mt-1">See which drops and affiliates pull traffic.</p>
        </div>
        <div className="flex gap-1">
          {RANGES.map(([d, l]) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`analytics-range-${d}`}
              className={`font-body uppercase tracking-wide2 text-[0.6rem] px-4 py-2 border transition-colors ${days === d ? "bg-ink text-linen border-ink" : "border-ink/20 text-ink/60 hover:border-ink"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {!data && <p className="font-body text-sm text-ink/40">Loading…</p>}

      {data && (
        <div className="space-y-8">
          <div className="adm-card flex items-center gap-4" data-testid="analytics-total-views">
            <div className="w-12 h-12 rounded-full bg-ink text-linen flex items-center justify-center"><Eye size={20} strokeWidth={1.5} /></div>
            <div>
              <p className="font-heading text-4xl leading-none">{data.total_views.toLocaleString()}</p>
              <p className="font-body uppercase tracking-wide2 text-[0.6rem] text-ink/50 mt-1">Storefront views · last {data.days} days</p>
            </div>
          </div>

          <div className="adm-card">
            <p className="adm-label mb-4">Views Over Time</p>
            {data.daily.length === 0 ? <p className="font-body text-sm text-ink/40">No views yet.</p> : (
              <div className="flex items-end gap-1 h-40" data-testid="analytics-daily-chart">
                {data.daily.map((d) => (
                  <div key={d.date} className="flex-1 bg-ink/80 hover:bg-ink transition-colors min-w-[3px]"
                    style={{ height: `${(d.views / maxDaily) * 100}%` }} title={`${d.date}: ${d.views}`} />
                ))}
              </div>
            )}
          </div>

          {scans && (
            <div className="adm-card" data-testid="analytics-qr-scans">
              <p className="adm-label mb-4 flex items-center gap-2"><QrCode size={13} /> QR Scans · {scans.total} total</p>
              {scans.by_src.length === 0 ? <p className="font-body text-sm text-ink/40">No scans yet — print your QR tags to start tracking which drive traffic.</p> : (
                <div className="space-y-2">{scans.by_src.map((s) => (<div key={s.src} className="flex justify-between font-body text-sm"><span className="text-ink/80 truncate pr-3">{s.src}</span><span className="font-heading text-base">{s.scans}</span></div>))}</div>
              )}
            </div>
          )}

          {shares && shares.items.length > 0 && (
            <div className="adm-card" data-testid="analytics-most-shared">
              <p className="adm-label mb-4 flex items-center gap-2"><Share2 size={13} /> Most Shared · {shares.total} total shares</p>
              <div className="space-y-2">{shares.items.slice(0, 10).map((s) => (
                <div key={s.key} className="flex justify-between font-body text-sm"><span className="text-ink/80 truncate pr-3"><span className="uppercase tracking-wide2 text-[0.55rem] text-ink/40 mr-2">{s.kind}</span>{s.title}</span><span className="font-heading text-base">{s.count}</span></div>
              ))}</div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <Panel title="Top Products" testid="analytics-top-products" rows={data.top_products}
              render={(r) => [r.title, r.views]} empty="No product views yet." />
            <Panel title="Top Referrers" testid="analytics-top-referrers" rows={data.top_referrers}
              render={(r) => [r.source, r.views]} empty="No referrer data yet." />
            <Panel title="Top Pages" testid="analytics-top-pages" rows={data.top_pages}
              render={(r) => [r.path, r.views]} empty="No page views yet." />
            <div className="adm-card" data-testid="analytics-affiliate-traffic">
              <p className="adm-label mb-4 flex items-center gap-2"><TrendingUp size={13} /> Affiliate Traffic</p>
              {data.affiliate_traffic.length === 0 ? <p className="font-body text-sm text-ink/40">No affiliate-referred traffic yet.</p> : (
                <table className="w-full font-body text-sm">
                  <thead><tr className="uppercase tracking-wide2 text-[0.55rem] text-ink/50 text-left"><th className="pb-2">Code</th><th className="pb-2 text-right">Views</th><th className="pb-2 text-right">Clicks</th><th className="pb-2 text-right">Sales</th></tr></thead>
                  <tbody>{data.affiliate_traffic.map((a) => (
                    <tr key={a.code} className="border-t border-ink/5"><td className="py-2">{a.code}{a.name ? <span className="text-ink/40"> · {a.name}</span> : ""}</td><td className="py-2 text-right">{a.views}</td><td className="py-2 text-right">{a.clicks}</td><td className="py-2 text-right">{a.conversions}</td></tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Panel({ title, testid, rows, render, empty }) {
  return (
    <div className="adm-card" data-testid={testid}>
      <p className="adm-label mb-4">{title}</p>
      {rows.length === 0 ? <p className="font-body text-sm text-ink/40">{empty}</p> : (
        <div className="space-y-2">
          {rows.map((r, i) => { const [label, val] = render(r); return (
            <div key={i} className="flex items-center justify-between font-body text-sm">
              <span className="truncate pr-3 text-ink/80">{label}</span>
              <span className="font-heading text-base">{val}</span>
            </div>
          ); })}
        </div>
      )}
    </div>
  );
}
